<script setup>
import { ref, watch, onMounted, nextTick } from 'vue';

const props = defineProps({
  modelValue: { type: String, default: '' },
  placeholder: { type: String, default: '' },
});
const emit = defineEmits(['update:modelValue']);

const editor = ref(null);
const htmlMode = ref(false);
const htmlText = ref('');

function sync() {
  if (editor.value) emit('update:modelValue', editor.value.innerHTML);
}

// Seçim editörden çıkmasın diye toolbar'da mousedown'ı engelliyoruz.
function exec(command, value = null) {
  document.execCommand(command, false, value);
  if (editor.value) editor.value.focus();
  sync();
}
function setBlock(e) {
  exec('formatBlock', e.target.value);
  e.target.value = 'p';
}
function makeLink() {
  // eslint-disable-next-line no-alert
  const url = window.prompt('Bağlantı adresi (URL):', 'https://');
  if (url) exec('createLink', url);
}
function onColor(e) {
  exec('foreColor', e.target.value);
}

function toggleHtml() {
  if (!htmlMode.value) {
    htmlText.value = editor.value ? editor.value.innerHTML : props.modelValue;
    htmlMode.value = true;
  } else {
    emit('update:modelValue', htmlText.value);
    htmlMode.value = false;
    nextTick(() => {
      if (editor.value) editor.value.innerHTML = htmlText.value;
    });
  }
}
function onHtmlInput() {
  emit('update:modelValue', htmlText.value);
}

watch(
  () => props.modelValue,
  v => {
    if (htmlMode.value) return;
    if (editor.value && editor.value.innerHTML !== (v || '')) {
      editor.value.innerHTML = v || '';
    }
  }
);

onMounted(() => {
  if (editor.value) editor.value.innerHTML = props.modelValue || '';
});
</script>

<template>
  <div
    class="rte rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-800"
  >
    <!-- Araç çubuğu -->
    <div
      class="flex flex-wrap items-center gap-0.5 p-1.5 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/80"
    >
      <select
        class="h-8 px-2 text-xs rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200"
        @mousedown.stop
        @change="setBlock"
      >
        <option value="p">Paragraf</option>
        <option value="h1">Başlık 1</option>
        <option value="h2">Başlık 2</option>
        <option value="h3">Başlık 3</option>
      </select>

      <span class="w-px h-5 bg-slate-200 dark:bg-slate-600 mx-1" />

      <button
        class="rte-btn"
        title="Kalın"
        @mousedown.prevent
        @click="exec('bold')"
      >
        <span class="i-lucide-bold size-4" />
      </button>
      <button
        class="rte-btn"
        title="İtalik"
        @mousedown.prevent
        @click="exec('italic')"
      >
        <span class="i-lucide-italic size-4" />
      </button>
      <button
        class="rte-btn"
        title="Altı çizili"
        @mousedown.prevent
        @click="exec('underline')"
      >
        <span class="i-lucide-underline size-4" />
      </button>
      <button
        class="rte-btn"
        title="Üstü çizili"
        @mousedown.prevent
        @click="exec('strikeThrough')"
      >
        <span class="i-lucide-strikethrough size-4" />
      </button>

      <label class="rte-btn cursor-pointer relative" title="Metin rengi">
        <span class="i-lucide-palette size-4" />
        <input
          type="color"
          class="absolute inset-0 opacity-0 cursor-pointer"
          @mousedown.stop
          @input="onColor"
        />
      </label>

      <span class="w-px h-5 bg-slate-200 dark:bg-slate-600 mx-1" />

      <button
        class="rte-btn"
        title="Sola hizala"
        @mousedown.prevent
        @click="exec('justifyLeft')"
      >
        <span class="i-lucide-align-left size-4" />
      </button>
      <button
        class="rte-btn"
        title="Ortala"
        @mousedown.prevent
        @click="exec('justifyCenter')"
      >
        <span class="i-lucide-align-center size-4" />
      </button>
      <button
        class="rte-btn"
        title="Sağa hizala"
        @mousedown.prevent
        @click="exec('justifyRight')"
      >
        <span class="i-lucide-align-right size-4" />
      </button>

      <span class="w-px h-5 bg-slate-200 dark:bg-slate-600 mx-1" />

      <button
        class="rte-btn"
        title="Madde işaretli liste"
        @mousedown.prevent
        @click="exec('insertUnorderedList')"
      >
        <span class="i-lucide-list size-4" />
      </button>
      <button
        class="rte-btn"
        title="Numaralı liste"
        @mousedown.prevent
        @click="exec('insertOrderedList')"
      >
        <span class="i-lucide-list-ordered size-4" />
      </button>
      <button
        class="rte-btn"
        title="Bağlantı"
        @mousedown.prevent
        @click="makeLink"
      >
        <span class="i-lucide-link size-4" />
      </button>
      <button
        class="rte-btn"
        title="Biçimi temizle"
        @mousedown.prevent
        @click="exec('removeFormat')"
      >
        <span class="i-lucide-remove-formatting size-4" />
      </button>

      <span class="flex-1" />

      <button
        class="rte-btn"
        :class="htmlMode ? 'bg-woot-100 text-woot-700' : ''"
        title="HTML ile düzenle"
        @mousedown.prevent
        @click="toggleHtml"
      >
        <span class="i-lucide-code size-4" />
      </button>
    </div>

    <!-- İçerik -->
    <textarea
      v-if="htmlMode"
      v-model="htmlText"
      class="w-full min-h-[200px] p-3 text-xs font-mono text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 focus:outline-none resize-y"
      spellcheck="false"
      @input="onHtmlInput"
    />
    <div
      v-show="!htmlMode"
      ref="editor"
      class="rte-content min-h-[200px] p-3 text-sm text-slate-800 dark:text-slate-100 focus:outline-none"
      contenteditable="true"
      :data-placeholder="placeholder"
      @input="sync"
      @blur="sync"
    />
  </div>
</template>

<style scoped>
.rte-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 0.375rem;
  color: rgb(71 85 105);
}
.rte-btn:hover {
  background: rgb(226 232 240);
}
.dark .rte-btn {
  color: rgb(203 213 225);
}
.dark .rte-btn:hover {
  background: rgb(51 65 85);
}
/* Tailwind preflight liste/başlık stillerini sıfırlıyor — editör içinde geri veriyoruz */
.rte-content :deep(h1) {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0.5em 0;
}
.rte-content :deep(h2) {
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0.5em 0;
}
.rte-content :deep(h3) {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0.5em 0;
}
.rte-content :deep(ul) {
  list-style: disc;
  padding-left: 1.5em;
  margin: 0.4em 0;
}
.rte-content :deep(ol) {
  list-style: decimal;
  padding-left: 1.5em;
  margin: 0.4em 0;
}
.rte-content :deep(a) {
  color: rgb(59 130 246);
  text-decoration: underline;
}
.rte-content :deep(p) {
  margin: 0.35em 0;
}
.rte-content:empty::before {
  content: attr(data-placeholder);
  color: rgb(148 163 184);
}
</style>
